import { useCallback, useEffect } from "react";
// referred const instead of HOC since this is a hook
import { useSelector } from "react-redux";

import { APPLICATION } from "@/constants";
import { RootState } from "@/store/root-reducer";

export interface ExtendedWindow extends Window {
  dataLayer?: {
    event_name: string;
    event: string;
    page_referrer: string | null;
    page_title: string | undefined;
    page_type: string | undefined;
    page_url: string | undefined;
    previous_policy_id?: string;
    quote_id: string | undefined;
    site_version?: string;
    user_id: string | undefined;
    user_status: string | undefined;
  }[];
}

type PageViewProps = {
  userId: string | undefined;
  quoteId: string | undefined;
  pageReferrer: string | null;
  pageUrl: string | undefined;
  pageTitle: string | undefined;
  previousPolicyId?: string;
};

const useGtmPageView = (props: PageViewProps) => {
  const {
    userId,
    quoteId,
    pageReferrer,
    pageUrl,
    pageTitle,
    previousPolicyId = "",
  } = props;

  const { attributes, referenceId } = useSelector(
    (state: RootState) => state.quote
  );
  const { gtmEvents } = attributes ?? {};

  let userIdMap = userId;
  if (APPLICATION === "renewal") {
    userIdMap = gtmEvents?.userId ?? userId ?? "";
  }

  const triggerPageView = useCallback(() => {
    const pageType = APPLICATION === "renewal" ? "renewals" : "quote & buy";
    const eventPageTitle =
      APPLICATION === "renewal" ? `Renewals | ${pageTitle}` : pageTitle;
    const extendedWindow = window as Partial<ExtendedWindow>;
    extendedWindow.dataLayer = extendedWindow.dataLayer ?? [];

    const baseData = {
      event: "page_load",
      event_name: "page_view",
      user_status: userIdMap ? "logged in" : "guest",
      user_id: userIdMap,
      quote_id: quoteId,
      page_type: pageType,
      page_referrer: pageReferrer,
      page_url: pageUrl,
      page_title: eventPageTitle,
    };

    if (APPLICATION === "renewal") {
      extendedWindow.dataLayer.push({
        ...baseData,
        previous_policy_id: previousPolicyId,
      });
    } else {
      extendedWindow.dataLayer.push({
        ...baseData,
        site_version: "1.5",
      });
    }
  }, [userIdMap, quoteId, pageReferrer, pageUrl, pageTitle, previousPolicyId]);

  // Quote & Buy application
  useEffect(() => {
    if (pageUrl && APPLICATION !== "renewal") triggerPageView();

    return () => {
      // Cleanup code if needed
    };
  }, [
    userIdMap,
    quoteId,
    pageReferrer,
    pageUrl,
    pageTitle,
    previousPolicyId,
    triggerPageView,
  ]);

  // Renewal application
  useEffect(() => {
    if (APPLICATION === "renewal" && referenceId && pageUrl) {
      triggerPageView();
    }

    // Cleanup code if needed
  }, [
    userIdMap,
    quoteId,
    pageReferrer,
    pageUrl,
    pageTitle,
    previousPolicyId,
    triggerPageView,
    referenceId,
  ]);
};

export default useGtmPageView;
